import type { ApplicationView } from './helpers';

/**
 * HyperCore's activation fee of a new account, 1 USDC: the engine's `domain.HyperCoreActivationFee()` (C-03 carries
 * it to the app as `activationFee`; C-06 has no field for it, so the console mirrors the constant).
 */
export const ACTIVATION_FEE_USDC = '1';

const SCALE = 6;

const toUnits = (value: string): bigint => {
  const [whole, fraction = ''] = value.trim().split('.');
  return BigInt(whole + fraction.padEnd(SCALE, '0').slice(0, SCALE));
};

const fromUnits = (units: bigint): string => {
  const text = units.toString().padStart(SCALE + 1, '0');
  const whole = text.slice(0, -SCALE);
  const fraction = text.slice(-SCALE).replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : whole;
};

/** `a + b` on two non-negative decimal strings, exactly at 6 dp, never through Number(). */
export const addDecimal = (a: string, b: string): string => fromUnits(toUnits(a) + toUnits(b));

type StakeRow = Pick<ApplicationView, 'status' | 'stake'>;

/**
 * What the manager sends so the stake arrives (XBIT-4400, D-30 amendment 2026-10-05). At CATALOGUED the vault account
 * does not exist on the venue yet: the stake's usdSend is its first transfer and loses the 1 USDC from its amount, so
 * stake + 1. From ACTIVATED on the account exists, no fee is taken, and the plain stake is right (f1's condition 2;
 * `received == null` alone would misfire when some other transfer activated the account first).
 */
export const stakeToSend = (r: StakeRow): string | null => {
  const expected = r.stake?.expected;
  if (!expected) return null;
  return r.status === 'CATALOGUED'
    ? addDecimal(expected, ACTIVATION_FEE_USDC)
    : addDecimal(expected, '0');
};

/**
 * The stake is owed while the application waits for it: CATALOGUED or ACTIVATED with nothing received (XBIT-4470). The
 * faucet's suggestion and the drawer's "No stake received yet" both read this one rule.
 */
export const stakeOwed = (r: StakeRow): boolean =>
  (r.status === 'CATALOGUED' || r.status === 'ACTIVATED') && !r.stake?.received;

/**
 * The faucet's suggested amount: what `stakeToSend` asks, while the stake is still owed (`stakeOwed`). Capped at the faucet's ceiling (`maxAmount`), with `capped` so the dialog can say the transfer lands short.
 * Null when there is nothing to suggest; the operator can always type an amount.
 */
export const faucetDefaultAmount = (
  r: StakeRow,
  maxAmount: string | null | undefined
): { amount: string; capped: boolean; needed: string } | null => {
  if (!stakeOwed(r)) return null;
  const amount = stakeToSend(r);
  if (!amount) return null;
  if (maxAmount && toUnits(amount) > toUnits(maxAmount)) {
    return { amount: addDecimal(maxAmount, '0'), capped: true, needed: amount };
  }
  return { amount, capped: false, needed: amount };
};

/**
 * The faucet's suggested user (XBIT-4469, D2): the application's manager while the stake is owed, since the stake
 * parks only when it is credited to the manager's holder. '' otherwise, or with no manager; the operator can always
 * type or edit it.
 */
export const faucetDefaultUserId = (
  r: Pick<ApplicationView, 'status' | 'stake' | 'manager'>
): string => (stakeOwed(r) ? (r.manager?.holderId ?? '') : '');

/** The manager's other parked transfers beside the stake (C-06 additionalCount / additionalTotal); null when none. */
export const alsoReceived = (
  r: Pick<ApplicationView, 'stake'>
): { count: number; total: string } | null => {
  const count = r.stake?.additionalCount ?? 0;
  if (count <= 0) return null;
  return { count, total: r.stake?.additionalTotal ?? '0' };
};
